import { useLayoutEffect, useRef, useState } from 'react';
import type { NostrMetadata } from '@nostrify/nostrify';
import { QRCodeSVG } from '@/components/ui/qrcode';
import { FontLoader } from '@/components/FontLoader';
import { cn } from '@/lib/utils';
import { hexToRgb, isDark, qrColors, type ProfileTheme } from '@/lib/profileTheme';
import { DEFAULT_QR_STYLE, type QrStyle } from '@/lib/qrStyles';
import { getAvatarShape, getEmojiMaskUrl } from '@/lib/avatarShape';
import { badgeImageUrl, type BadgeData } from '@/lib/badges';
import {
  cardSize,
  DEFAULT_FIELD_VISIBILITY,
  type CardFieldVisibility,
  type Orientation,
} from '@/lib/cardFormat';
import {
  displayUrl as stripProtocol,
  resolveQrUrl,
  DEFAULT_QR_TARGET,
  type ProfileLink,
  type QrTarget,
} from '@/lib/cardLinks';
import { faviconUrl } from '@/lib/faviconUrl';
import { sanitizeImageUrl } from '@/lib/sanitizeUrl';
import { Award, Globe, Zap, AtSign, Bitcoin, Hash, Link as LinkIcon, RotateCw } from 'lucide-react';

/**
 * How the avatar is cut out. Ditto profiles can carry a `shape` emoji; a card
 * is a more formal object than a feed, so the owner can always fall back to a
 * plain circle without editing their profile.
 */
export type AvatarShapeMode = 'shape' | 'circle';

/**
 * Which mark the website row wears. The generic globe is the default because
 * it inherits the theme's accent color and prints as cleanly as every other
 * row; the site's real favicon is more recognizable but drops a foreign
 * palette into an otherwise uniform strip, so it's opt-in.
 */
export type WebsiteIconMode = 'glyph' | 'favicon';

export interface BusinessCardData {
  npub: string;
  metadata?: NostrMetadata;
  theme: ProfileTheme;
  /** Optional custom headline to override the profile's about/title */
  headline?: string;
  /** Optional custom name to override the profile's display name */
  nameOverride?: string;
  /** Optional free-form extra text shown on the card (roomiest on vertical) */
  extraText?: string;
  /** Which profile fields to display */
  fields?: CardFieldVisibility;
  /** Circle, or the profile's own `shape` emoji when it has one */
  avatarShape?: AvatarShapeMode;
  /** Globe glyph, or the site's own favicon, on the website row */
  websiteIcon?: WebsiteIconMode;
  /** NIP-58 badges the profile has accepted, in the owner's order */
  badges?: BadgeData[];
  /** Extra link rows (additional kind-0 URLs, or ones typed by hand) */
  extraLinks?: ProfileLink[];
  /** Module shape for the QR code. Colors always come from the theme. */
  qrStyle?: QrStyle;
  /** Where the QR code points */
  qrTarget?: QrTarget;
  /** Print the destination address under the code (default: yes) */
  showQrUrl?: boolean;
  /** Punch the destination's favicon into the middle of the code */
  qrLogo?: boolean;
}

interface BusinessCardProps extends BusinessCardData {
  orientation: Orientation;
  flipped: boolean;
  onFlip: () => void;
  className?: string;
}

/**
 * The card is authored at its intrinsic print dimensions so every element can
 * use real, print-scale sizing. When the available width is smaller we shrink
 * the whole thing uniformly, which keeps the design identical instead of
 * reflowing full-size text into a cramped box.
 */
function useFitScale(intrinsicWidth: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const available = el.clientWidth;
      if (available > 0) setScale(Math.min(1, available / intrinsicWidth));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [intrinsicWidth]);

  return { ref, scale };
}

export function BusinessCard(props: BusinessCardProps) {
  const { orientation, flipped, onFlip, theme, className } = props;

  const { width, height } = cardSize(orientation);
  const { ref, scale } = useFitScale(width);

  return (
    <div className={cn('flex w-full min-w-0 flex-col items-center gap-4', className)}>
      <FontLoader fonts={[theme.bodyFont, theme.titleFont]} />
      <div
        ref={ref}
        className="relative w-full min-w-0 [perspective:1800px]"
        style={{ maxWidth: width, height: height * scale }}
      >
        {/* Uniform downscale. Kept separate from the flip so the rotation still
            happens about the card's own center.

            Taken out of flow on purpose: at full print width this box is 640px,
            and an in-flow element that wide would set a 640px min-content floor
            on every ancestor — which is exactly what pushed the layout off the
            side of a phone screen. Out of flow, it contributes no intrinsic
            width, so the measured `scale` is free to shrink the card to fit. */}
        <div
          className="absolute left-0 top-0"
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            transformStyle: 'preserve-3d',
            WebkitTransformStyle: 'preserve-3d',
          }}
        >
          <button
            type="button"
            onClick={onFlip}
            aria-label={flipped ? 'Show front of card' : 'Show back of card'}
            className={cn(
              'relative block h-full w-full rounded-2xl outline-none transition-transform duration-700 ease-in-out',
              'focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
            )}
            style={{
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              transformStyle: 'preserve-3d',
              WebkitTransformStyle: 'preserve-3d',
            }}
          >
            {/* Each face is its own absolutely-positioned 3D layer.
                `backface-visibility: hidden` hides a face when it is turned
                away from the viewer. The front sits at rotateY(0), the back is
                pre-rotated 180deg so it reads correctly once the card flips.

                Opacity guard: as a robust fallback for browsers that mishandle
                `backface-visibility`, the hidden face is also faded out. The
                fade is delayed to the flip midpoint so it isn't visible during
                the rotation. */}
            <CardFacePlate
              {...props}
              side="front"
              className="absolute inset-0 shadow-xl"
              // An explicit identity rotation is not cosmetic: without a 3D
              // transform of its own, browsers (Chrome) don't promote the front
              // face into the 3D rendering context, so its `backface-visibility`
              // is ignored and the mirrored back leaks through mid-flip. Giving
              // it `rotateY(0deg)` makes it a real 3D layer like the back face.
              style={{ ...facing3d, transform: 'rotateY(0deg)', opacity: flipped ? 0 : 1 }}
            />
            <CardFacePlate
              {...props}
              side="back"
              className="absolute inset-0 shadow-xl"
              style={{
                ...facing3d,
                transform: 'rotateY(180deg)',
                opacity: flipped ? 1 : 0,
              }}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What makes a plate behave as one side of a 3D card: it never shows its own
 * back, and the opacity guard (see `BusinessCard`) switches at the midpoint of
 * the rotation rather than fading across it.
 */
const facing3d: React.CSSProperties = {
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  transition: 'opacity 0s linear 350ms',
};

export interface CardFacePlateProps extends BusinessCardData {
  orientation: Orientation;
  side: 'front' | 'back';
  className?: string;
  style?: React.CSSProperties;
}

/**
 * One printed face of the card, on its own.
 *
 * Deliberately flat: it draws the background, the border and the content, but
 * owns no position, shadow or 3D transform — those come from the caller. That
 * is what lets the same markup serve both the flipping card and the off-screen
 * stage the PNG export rasterizes, with no second implementation to keep in
 * sync.
 */
export function CardFacePlate(props: CardFacePlateProps) {
  const {
    side, orientation, theme, metadata, npub, headline, nameOverride, extraText,
    fields = DEFAULT_FIELD_VISIBILITY, extraLinks = [], badges = [],
    avatarShape = 'shape', websiteIcon = 'glyph',
    qrStyle = DEFAULT_QR_STYLE, qrTarget = DEFAULT_QR_TARGET,
    showQrUrl = true, qrLogo = false,
  } = props;

  const bodyFontFamily = theme.bodyFont?.family
    ? `'${theme.bodyFont.family}', ui-sans-serif, system-ui, sans-serif`
    : 'ui-sans-serif, system-ui, sans-serif';
  const titleFontFamily = theme.titleFont?.family
    ? `'${theme.titleFont.family}', ${bodyFontFamily}`
    : bodyFontFamily;

  const dark = isDark(theme.background);
  const subtle = dark
    ? `rgba(255, 255, 255, 0.08)`
    : `rgba(0, 0, 0, 0.06)`;
  const border = dark
    ? `rgba(255, 255, 255, 0.14)`
    : `rgba(0, 0, 0, 0.10)`;
  const mutedText = dark
    ? `rgba(${hexToRgb(theme.text)}, 0.78)`
    : `rgba(${hexToRgb(theme.text)}, 0.72)`;

  const bgImage = theme.bg?.url;
  const bgLayer: React.CSSProperties = bgImage
    ? {
        backgroundImage: `url(${JSON.stringify(bgImage)})`,
        backgroundSize: theme.bg?.mode === 'tile' ? 'auto' : 'cover',
        backgroundRepeat: theme.bg?.mode === 'tile' ? 'repeat' : 'no-repeat',
        backgroundPosition: 'center',
      }
    : {};

  // The caller's own style wins: it is what turns a plate into a 3D layer of
  // the flipping card, or into a fixed-size tile on the export stage.
  const faceStyle: React.CSSProperties = {
    backgroundColor: theme.background,
    color: theme.text,
    fontFamily: bodyFontFamily,
    borderColor: border,
    ...bgLayer,
    ...props.style,
  };

  // `relative` is the plate's own business: the background scrim and the
  // content both position against the face, whether or not the caller has
  // placed the plate absolutely.
  const faceBase = cn(
    'relative rounded-2xl border overflow-hidden text-left flex flex-col',
    props.className,
  );

  const name = nameOverride?.trim()
    || metadata?.display_name
    || metadata?.name
    || 'Anonymous';
  // Fields respect the visibility toggles. Avatar/bio can be hidden too.
  const picture = fields.avatar ? sanitizeImageUrl(metadata?.picture) : undefined;
  const shape = fields.avatar && avatarShape === 'shape'
    ? getAvatarShape(metadata)
    : undefined;
  const about = fields.bio ? (headline?.trim() || metadata?.about) : undefined;
  const nip05 = fields.nip05 ? metadata?.nip05 : undefined;
  const website = fields.website ? metadata?.website : undefined;
  const lud16 = fields.lud16 ? metadata?.lud16 : undefined;
  const extra = extraText?.trim();

  const qrUrl = resolveQrUrl(qrTarget, {
    website: metadata?.website,
    nip05: metadata?.nip05,
    npub,
  });

  if (side === 'front') {
    return (
      <div className={faceBase} style={faceStyle}>
        {bgImage && (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: `rgba(${hexToRgb(theme.background)}, 0.55)` }}
          />
        )}
        <FrontContent
          orientation={orientation}
          name={name}
          about={about}
          extra={extra}
          picture={picture}
          shape={shape}
          theme={theme}
          titleFontFamily={titleFontFamily}
          mutedText={mutedText}
          subtle={subtle}
          nip05={nip05}
          website={website}
          websiteIcon={websiteIcon}
          lud16={lud16}
          extraLinks={extraLinks}
          badges={fields.badges ? badges : []}
        />
      </div>
    );
  }

  return (
    <div className={faceBase} style={faceStyle}>
      {bgImage && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(${hexToRgb(theme.background)}, 0.7)` }}
        />
      )}
      <BackContent
        orientation={orientation}
        qrUrl={qrUrl}
        theme={theme}
        mutedText={mutedText}
        subtle={subtle}
        qrStyle={qrStyle}
        showQrUrl={showQrUrl}
        qrLogo={qrLogo}
      />
    </div>
  );
}

/**
 * Avatar with optional Ditto shape support: when the profile carries a `shape`
 * emoji in its kind-0 metadata, the picture is masked to that glyph instead of
 * a circle. The accent ring is drawn with stacked drop-shadows so it hugs the
 * silhouette — a `box-shadow` would be clipped away by the mask.
 */
function CardAvatar({
  name,
  picture,
  shape,
  size,
  theme,
  subtle,
}: {
  name: string;
  picture?: string;
  shape?: string;
  size: number;
  theme: ProfileTheme;
  subtle: string;
}) {
  const maskUrl = shape ? getEmojiMaskUrl(shape) : '';
  const ring = Math.max(3, Math.round(size * 0.028));

  const maskStyle: React.CSSProperties = maskUrl
    ? {
        WebkitMaskImage: `url(${maskUrl})`,
        maskImage: `url(${maskUrl})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }
    : { boxShadow: `0 0 0 ${ring}px ${theme.primary}` };

  const inner = (
    <div
      className={cn('relative shrink-0 overflow-hidden', !maskUrl && 'rounded-full')}
      style={{ width: size, height: size, ...maskStyle }}
    >
      {picture ? (
        <img src={picture} alt={name} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center font-bold"
          style={{
            backgroundColor: subtle,
            color: theme.primary,
            fontSize: Math.round(size * 0.38),
          }}
        >
          {name.slice(0, 2).toUpperCase()}
        </div>
      )}
    </div>
  );

  if (!maskUrl) return inner;

  return (
    <div
      className="shrink-0"
      style={{
        filter: [
          `drop-shadow(${ring}px 0 0 ${theme.primary})`,
          `drop-shadow(-${ring}px 0 0 ${theme.primary})`,
          `drop-shadow(0 ${ring}px 0 ${theme.primary})`,
          `drop-shadow(0 -${ring}px 0 ${theme.primary})`,
        ].join(' '),
      }}
    >
      {inner}
    </div>
  );
}

interface FrontContentProps {
  orientation: Orientation;
  name: string;
  about?: string;
  extra?: string;
  picture?: string;
  shape?: string;
  theme: ProfileTheme;
  titleFontFamily: string;
  mutedText: string;
  subtle: string;
  nip05?: string;
  website?: string;
  websiteIcon: WebsiteIconMode;
  lud16?: string;
  extraLinks: ProfileLink[];
  badges: BadgeData[];
}

/**
 * A bio is authored for a feed, not for a card. Its own line breaks are worth
 * keeping — they're usually deliberate structure — but the blank lines between
 * paragraphs are pure air, and on a card every line of the clamp is precious.
 * So: keep single breaks, drop the empty lines between them.
 */
function normalizeBio(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n\s*\n+/g, '\n')
    .trim();
}

/** Tailwind's `leading-snug`, which every block of card body copy uses. */
const LINE_RATIO = 1.375;

/**
 * Fit the bio to the room it actually has.
 *
 * Deliberately not `line-clamp`: that draws an ellipsis, and a trailing "…" on
 * a printed card reads as damage rather than as brevity. A plain `max-height`
 * floored to a whole number of lines cuts cleanly at a line boundary instead,
 * so the last visible line is complete and nothing is sliced in half.
 */
function bioBudget(available: number, fontSize: number): React.CSSProperties {
  const lineHeight = fontSize * LINE_RATIO;
  const lines = Math.max(1, Math.floor(available / lineHeight));
  return { maxHeight: lines * lineHeight, overflow: 'hidden' };
}

/**
 * The bio is body copy, not a caption, so it holds full size until there is
 * enough of it that fitting more words per line beats a point of type size.
 */
function bioFontSize(text: string, max: number): number {
  if (text.length <= 120) return max;
  if (text.length <= 220) return max - 2;
  return max - 4;
}

/**
 * Room the vertical card's free-form block will claim: its own padding plus
 * however many 20px lines the text wraps to, at roughly 33 characters to a
 * line at that width. An estimate, but only ever used to leave the bio a
 * conservative amount of space.
 */
function extraBlockHeight(extra?: string): number {
  if (!extra) return 0;
  const lines = extra
    .split('\n')
    .reduce((total, line) => total + Math.max(1, Math.ceil(line.length / 33)), 0);
  return 32 + Math.min(4, lines) * 20 * LINE_RATIO + 20;
}

function FrontContent(props: FrontContentProps) {
  const {
    orientation, name, extra, picture, shape, theme,
    titleFontFamily, mutedText, subtle, nip05, website, websiteIcon, lud16, extraLinks, badges,
  } = props;

  const vertical = orientation === 'vertical';
  const about = props.about ? normalizeBio(props.about) : undefined;

  // Contact rows are the small print, and small print is exactly what was
  // unreadable before. They now run at real body-copy size, and the row size
  // eases down only when a profile has enough links to need the room. A badge
  // strip claims about as much height as one more row, so it counts as one.
  const rowCount = [nip05, website, lud16].filter(Boolean).length
    + extraLinks.length
    + (badges.length ? 1 : 0);
  const contactSize = rowCount > 4 ? 18 : rowCount > 3 ? 20 : 22;
  const iconSize = Math.round(contactSize * 1.15);

  // A fixed line clamp can't tell a crowded card from an empty one, so it cut
  // bios short on cards with acres of space left. Price the furniture instead —
  // name, badge strip, contact rows, the optional block — and hand every
  // remaining pixel to the bio.
  const pad = vertical ? 36 : 32;
  const contentHeight = cardSize(orientation).height - pad * 2;
  const contactBlockH = rowCount
    ? rowCount * (contactSize * 1.3) + (rowCount - 1) * (vertical ? 12 : 10) + (vertical ? 20 : 16)
    : 0;
  const badgeBlockH = badges.length ? (vertical ? 44 + 20 : 38 + 16) : 0;
  const bioSize = about ? bioFontSize(about, vertical ? 21 : 20) : 20;
  const bioAvail = vertical
    // Avatar and its gap, one line of name, the `mt-3` above the bio, and the
    // free-form block when there is one.
    ? contentHeight - (196 + 20) - 50 - 12 - badgeBlockH - contactBlockH - extraBlockHeight(extra)
    // Horizontal stacks only the name and its `mt-2` above the bio.
    : contentHeight - (48 + 8) - badgeBlockH - contactBlockH;

  const avatar = (
    <CardAvatar
      name={name}
      picture={picture}
      shape={shape}
      size={vertical ? 196 : 156}
      theme={theme}
      subtle={subtle}
    />
  );

  const contactRows = (
    <>
      {nip05 && (
        <ContactRow icon={<AtSign size={iconSize} />} color={theme.primary} muted={mutedText}>
          {nip05.replace(/^_@/, '')}
        </ContactRow>
      )}
      {website && (
        <ContactRow
          icon={
            websiteIcon === 'favicon' ? (
              <FaviconIcon
                url={website}
                size={iconSize}
                color={theme.primary}
                fallback={<Globe size={iconSize} />}
              />
            ) : (
              <Globe size={iconSize} />
            )
          }
          color={theme.primary}
          muted={mutedText}
        >
          {stripProtocol(website)}
        </ContactRow>
      )}
      {lud16 && (
        <ContactRow icon={<Zap size={iconSize} />} color={theme.primary} muted={mutedText}>
          {lud16}
        </ContactRow>
      )}
      {extraLinks.map((link) => (
        <ContactRow
          key={link.id}
          icon={
            link.url ? (
              // The site's own mark identifies it far faster than a generic
              // chain link, exactly as Ditto renders external links.
              <FaviconIcon
                url={link.url}
                size={iconSize}
                color={theme.primary}
                fallback={<LinkIcon size={iconSize} />}
              />
            ) : link.bitcoin ? (
              <Bitcoin size={iconSize} />
            ) : (
              <Hash size={iconSize} />
            )
          }
          color={theme.primary}
          muted={mutedText}
        >
          {/* A URL speaks for itself; anything else (a payment address, a
              handle) is meaningless without the label the profile gave it. */}
          {link.url ? link.text : `${link.label}: ${link.text}`}
        </ContactRow>
      ))}
    </>
  );

  if (orientation === 'horizontal') {
    return (
      // Avatar and text travel together as one block, centered in the face.
      // The column is sized by its own content rather than stretched to fill,
      // so a sparse profile sits in the middle of the card instead of being
      // pinned to the left edge with a void beside it.
      <div className="relative z-10 flex h-full w-full items-center justify-center gap-10 p-8">
        {avatar}
        <div className="flex min-w-0 flex-col justify-center gap-4">
          <div className="min-w-0">
            <h2
              className="truncate font-bold leading-[1.05]"
              style={{ fontFamily: titleFontFamily, color: theme.text, fontSize: 46 }}
            >
              {name}
            </h2>
            {about && (
              <p
                className="mt-2 whitespace-pre-line leading-snug"
                style={{ color: mutedText, fontSize: bioSize, ...bioBudget(bioAvail, bioSize) }}
              >
                {about}
              </p>
            )}
          </div>
          {badges.length > 0 && (
            <BadgeRow badges={badges} size={38} max={6} subtle={subtle} />
          )}
          <div
            className="flex flex-col gap-2.5"
            style={{ fontSize: contactSize }}
          >
            {contactRows}
          </div>
        </div>
      </div>
    );
  }

  // Vertical: a balanced centered column that distributes its content evenly
  // across the full height, rather than clustering everything at the top with
  // an awkward void in the middle.
  return (
    <div className="relative z-10 flex h-full w-full flex-col items-center justify-between gap-5 p-9 text-center">
      {/* Top: identity */}
      <div className="flex w-full flex-col items-center gap-5">
        {avatar}
        <div className="w-full min-w-0">
          <h2
            className="line-clamp-2 font-bold leading-[1.05]"
            style={{ fontFamily: titleFontFamily, color: theme.text, fontSize: 48 }}
          >
            {name}
          </h2>
          {about && (
            <p
              className="mt-3 whitespace-pre-line leading-snug"
              style={{ color: mutedText, fontSize: bioSize, ...bioBudget(bioAvail, bioSize) }}
            >
              {about}
            </p>
          )}
        </div>
        {badges.length > 0 && (
          <BadgeRow badges={badges} size={44} max={5} subtle={subtle} centered />
        )}
      </div>

      {/* Middle: optional free-form block */}
      {extra && (
        <div
          className="w-full whitespace-pre-line rounded-2xl px-5 py-4 leading-snug"
          style={{ backgroundColor: subtle, color: mutedText, fontSize: 20 }}
        >
          {extra}
        </div>
      )}

      {/* Bottom: contact details */}
      <div
        className="flex w-full flex-col gap-3 text-left"
        style={{ fontSize: contactSize }}
      >
        {contactRows}
      </div>
    </div>
  );
}

/**
 * A strip of the profile's accepted NIP-58 badges, in the order the owner
 * arranged them — the same showcase Ditto renders, reduced to artwork only.
 * Names are dropped: at card scale a row of legible labels would cost more
 * height than the badges themselves, and the artwork is the recognizable part.
 */
function BadgeRow({
  badges,
  size,
  max,
  subtle,
  centered,
}: {
  badges: BadgeData[];
  size: number;
  /** Extras past this are simply left off — a "+N" chip is UI, not print. */
  max: number;
  subtle: string;
  centered?: boolean;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', centered && 'justify-center')}>
      {badges.slice(0, max).map((badge) => (
        <BadgeTile key={badge.aTag} badge={badge} size={size} subtle={subtle} />
      ))}
    </div>
  );
}

function BadgeTile({
  badge,
  size,
  subtle,
}: {
  badge: BadgeData;
  size: number;
  subtle: string;
}) {
  const [failed, setFailed] = useState(false);
  const src = badgeImageUrl(badge, size);

  if (!src || failed) {
    return (
      <span
        className="flex items-center justify-center rounded-lg"
        style={{ width: size, height: size, backgroundColor: subtle }}
      >
        <Award size={Math.round(size * 0.55)} />
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={badge.name}
      title={badge.name}
      className="rounded-lg object-cover"
      style={{ width: size, height: size }}
      onError={() => setFailed(true)}
      loading="lazy"
      decoding="async"
    />
  );
}

/**
 * A link's own favicon, fetched through the same service Ditto uses, with a
 * lucide glyph behind it. The favicon is decorative — if the service or the
 * site has nothing to give, we fall back rather than leaving a hole in the row.
 */
export function FaviconIcon({
  url,
  size,
  color,
  fallback,
}: {
  url: string;
  size: number;
  color: string;
  fallback: React.ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  const src = faviconUrl(url);

  if (!src || failed) {
    return <span style={{ color }}>{fallback}</span>;
  }

  return (
    <img
      src={src}
      alt=""
      className="object-contain"
      style={{ width: size, height: size }}
      onError={() => setFailed(true)}
      decoding="async"
    />
  );
}

function ContactRow({
  icon,
  color,
  muted,
  children,
}: {
  icon: React.ReactNode;
  color: string;
  muted: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0" style={{ color }}>{icon}</span>
      <span className="truncate font-medium" style={{ color: muted }}>{children}</span>
    </div>
  );
}

/**
 * Printed URLs are meant to be read across a desk, so the caption starts large
 * and only steps down for addresses long enough to need it.
 */
function urlFontSize(url: string, max: number): number {
  if (url.length <= 22) return max;
  if (url.length <= 34) return max - 6;
  if (url.length <= 52) return max - 12;
  return max - 16;
}

interface BackContentProps {
  orientation: Orientation;
  /** URL encoded into the QR code */
  qrUrl: string;
  theme: ProfileTheme;
  mutedText: string;
  subtle: string;
  qrStyle: QrStyle;
  /** Print the destination address beneath the code */
  showQrUrl: boolean;
  /** Punch the destination's favicon into the middle of the code */
  qrLogo: boolean;
}

/**
 * The back is deliberately just the code and the address it resolves to — the
 * name already lives on the front, so repeating it only shrinks the QR. The
 * printed text is derived from the encoded URL, so the two can never disagree.
 */
function BackContent(props: BackContentProps) {
  const { orientation, qrUrl, theme, mutedText, subtle, qrStyle, showQrUrl, qrLogo } = props;
  const qr = qrColors(theme);
  const vertical = orientation === 'vertical';
  const text = stripProtocol(qrUrl);

  const plate = (size: number) => (
    <div
      className="relative shrink-0 rounded-2xl p-3"
      style={{ backgroundColor: qr.bg, boxShadow: `0 0 0 1px ${subtle}` }}
    >
      <QRCodeSVG
        value={qrUrl}
        size={size}
        // A center logo covers modules, so the code is generated with the
        // highest error correction whenever one is drawn — 30% redundancy
        // against a patch that occupies well under a tenth of the area.
        level={qrLogo ? 'H' : 'Q'}
        style={qrStyle}
        fgColor={qr.fg}
        bgColor={qr.bg}
      />
      {qrLogo && (
        <span
          className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl"
          style={{
            width: Math.round(size * 0.24),
            height: Math.round(size * 0.24),
            backgroundColor: qr.bg,
          }}
        >
          <FaviconIcon
            url={qrUrl}
            size={Math.round(size * 0.17)}
            color={qr.fg}
            fallback={<Globe size={Math.round(size * 0.15)} />}
          />
        </span>
      )}
    </div>
  );

  // Without a caption the code is the only thing on the face, so it grows into
  // the room the text would have taken rather than floating in a wide margin.
  const size = vertical
    ? (showQrUrl ? 352 : 400)
    : (showQrUrl ? 236 : 280);

  // Stacked on both orientations: the address reads as a caption *under* the
  // code rather than as a column beside it.
  return (
    <div
      className={cn(
        'relative z-10 flex h-full w-full flex-col items-center justify-center text-center',
        vertical ? 'gap-8 p-8' : 'gap-4 p-6',
      )}
    >
      {plate(size)}
      {showQrUrl && (
        <p
          className="max-w-full break-words font-semibold leading-tight"
          style={{ color: mutedText, fontSize: urlFontSize(text, vertical ? 32 : 26) }}
        >
          {text}
        </p>
      )}
    </div>
  );
}

/** A small hint shown beneath the card. */
export function FlipHint({ onFlip }: { onFlip: () => void }) {
  return (
    <button
      type="button"
      onClick={onFlip}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-2 py-1"
    >
      <RotateCw className="h-4 w-4" />
      Tap card to flip
    </button>
  );
}
